import React, { useState, useEffect } from 'react';
import {
  getAIStrategies, getAIStrategy, createAIStrategy, updateAIStrategy,
  deleteAIStrategy, copyAIStrategy, setDefaultAIStrategy,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import { parseApiError } from '../../utils/parseApiError';

/* ============================================================
   AI 策略管理（V5 对齐版）
   ------------------------------------------------------------
   V5 测评出题已完全由 backend/apps/assessment/round_plan.py 硬编码
   30/60/90 题（quick/standard/deep）；V4 时代的题量、多选计分、4 段
   出题 prompt、AI 微调、题干改写、画像 schema 等字段一律不再生效。

   本页只保留 V5 主链路真生效的可调字段：
     · 策略元信息：name / version / is_default
     · AI 出题运行时（v5_question_generator 真读）：
       depth_ai_model / depth_ai_timeout_sec / depth_ai_max_retries
     · 学员报告课程推送 hook：enable_student_course_recommendation
   ============================================================ */

const EMPTY_FORM = {
  name: '',
  version: 1,
  is_default: false,
  depth_ai_model: '',
  depth_ai_timeout_sec: 30,
  depth_ai_max_retries: 3,
  enable_student_course_recommendation: false,
};

// V5 固定题量结构（与 backend/apps/assessment/round_plan.py 对齐）
const V5_FIXED_PLAN = [
  { label: '快速版', total: 30, ability: 24, trait: 6,
    detail: '3 轮能力（R1/R2 题库 + R3 AI）+ 1 批素质 = 30 题' },
  { label: '标准版', total: 60, ability: 48, trait: 12,
    detail: '6 轮能力（R3/R6 AI）+ 2 批素质 = 60 题' },
  { label: '深度版', total: 90, ability: 72, trait: 18,
    detail: '9 轮能力（R3/R6/R9 AI）+ 3 批素质 = 90 题' },
];

export default function AdminAIStrategyPage() {
  const [strategies, setStrategies] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editItem, setEditItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [loadFailed, setLoadFailed] = useState(false);
  const loadList = () => {
    setListLoading(true);
    setLoadFailed(false);
    getAIStrategies()
      .then(({ data }) => setStrategies(data.results ?? data))
      .catch((e) => {
        setError(parseApiError(e, { fallback: '加载策略列表失败' }));
        setLoadFailed(true);
      })
      .finally(() => setListLoading(false));
  };

  useEffect(() => { loadList(); }, []);

  const openCreate = () => {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = async (strategy) => {
    setDetailLoading(true);
    setShowModal(true);
    try {
      const { data } = await getAIStrategy(strategy.id);
      setEditItem(data);
      // 后端可能返回老数据缺新字段,统一兜底到 EMPTY_FORM 默认值
      setForm({ ...EMPTY_FORM, ...data });
    } catch { setError('加载策略详情失败'); setShowModal(false); }
    finally { setDetailLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('策略名称不能为空'); return; }
    setSaving(true); setError('');
    try {
      if (editItem) {
        await updateAIStrategy(editItem.id, form);
        setSuccess('修改成功');
      } else {
        await createAIStrategy(form);
        setSuccess('创建成功');
      }
      setShowModal(false);
      loadList();
    } catch (e) {
      const data = e.response?.data || {};
      if (data.detail) {
        setError(data.detail);
      } else {
        const firstField = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k][0]);
        setError(firstField ? `${firstField}: ${data[firstField][0]}` : '保存失败');
      }
    } finally { setSaving(false); }
  };

  const handleCopy = async (strategy) => {
    try {
      await copyAIStrategy(strategy.id);
      setSuccess(`已复制「${strategy.name}」`);
      loadList();
    } catch (e) { setError(e.response?.data?.detail || '复制失败'); }
  };

  const handleSetDefault = async (strategy) => {
    if (!window.confirm(`将「${strategy.name}」设为全局默认策略？`)) return;
    try {
      const { data } = await setDefaultAIStrategy(strategy.id);
      setSuccess(data.detail);
      loadList();
    } catch (e) { setError(e.response?.data?.detail || '操作失败'); }
  };

  const handleDelete = async (strategy) => {
    if (!window.confirm(`确认删除策略「${strategy.name}」？`)) return;
    try {
      await deleteAIStrategy(strategy.id);
      setSuccess('已删除');
      loadList();
    } catch (e) { setError(e.response?.data?.detail || '删除失败'); }
  };

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  return (
    <AdminLayout title="AI 策略管理">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>共 {strategies.length} 条策略</span>
        <Button size="sm" style={{ marginLeft: 'auto' }} onClick={openCreate}>+ 新建策略</Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {listLoading ? <p style={{ color: '#94a3b8' }}>加载中...</p> :
          loadFailed ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <p style={{ color: '#94a3b8', marginBottom: 12 }}>策略列表加载失败</p>
              <Button size="sm" variant="secondary" onClick={loadList}>重新加载</Button>
            </div>
          ) :
          strategies.length === 0 ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: 32 }}>暂无策略，请创建第一条</p> :
          strategies.map(s => (
            <div key={s.id} style={{
              background: '#fff', borderRadius: 12, padding: '18px 24px',
              border: `1.5px solid ${s.is_default ? '#2563eb' : '#e2e8f0'}`,
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>v{s.version}</span>
                  {s.is_default && (
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                      background: '#eff6ff', color: '#2563eb',
                    }}>全局默认</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {s.created_by_email && `创建人：${s.created_by_email} · `}
                  更新于 {new Date(s.updated_at).toLocaleDateString('zh-CN')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {!s.is_default && (
                  <button onClick={() => handleSetDefault(s)} style={actionBtn('#2563eb')}>设为默认</button>
                )}
                <button onClick={() => handleCopy(s)} style={actionBtn('#7c3aed')}>复制</button>
                <button onClick={() => openEdit(s)} style={actionBtn('#0891b2')}>编辑</button>
                {!s.is_default && (
                  <button onClick={() => handleDelete(s)} style={actionBtn('#dc2626')}>删除</button>
                )}
              </div>
            </div>
          ))
        }
      </div>

      {showModal && (
        <div onClick={() => setShowModal(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '40px 20px', overflowY: 'auto',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, padding: 28,
            width: '100%', maxWidth: 720,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                {editItem ? `编辑策略：${editItem.name}` : '新建策略'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>

            {detailLoading ? (
              <p style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>加载中...</p>
            ) : (
              <>
                {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}

                {/* 基础信息 */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>策略名称 *</label>
                    <input value={form.name} onChange={e => set('name', e.target.value)} style={inputStyle} placeholder="如：企业标准版 v2" />
                  </div>
                  <div>
                    <label style={labelStyle}>版本号</label>
                    <input type="number" min={1} value={form.version} onChange={e => set('version', +e.target.value)} style={inputStyle} />
                  </div>
                </div>

                {/* V5 固定题量说明（不可改） */}
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <p style={{ ...labelStyle, margin: 0 }}>测评题量（V5 固定）</p>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                      background: '#e0e7ff', color: '#4338ca',
                    }}>由 round_plan.py 硬编码</span>
                  </div>
                  <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 10px' }}>
                    V5 题量结构固定：8 能力维度按 8 题/轮滚动 + 3 素质按 6 题/批生成；
                    AI 出题永远开（每 3 轮第 3 轮 + 素质批全 AI）。
                  </p>
                  {V5_FIXED_PLAN.map(p => (
                    <div key={p.label} style={{
                      display: 'grid', gridTemplateColumns: '1fr auto', gap: 8,
                      padding: '6px 4px', borderTop: '1px solid #f1f5f9', fontSize: 12,
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#334155' }}>{p.label}</div>
                        <div style={{ color: '#94a3b8', fontSize: 10.5 }}>{p.detail}</div>
                      </div>
                      <div style={{ textAlign: 'right', color: '#475569', alignSelf: 'center' }}>
                        <b>{p.total} 题</b>
                        <span style={{ color: '#94a3b8', marginLeft: 6 }}>
                          {p.ability} 能力 + {p.trait} 素质
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* AI 出题运行时配置（V5 主链路 v5_question_generator 真读） */}
                <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #e2e8f0' }}>
                  <p style={{ ...labelStyle, marginBottom: 4 }}>AI 出题运行时</p>
                  <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 12px' }}>
                    v5_question_generator 调用智谱 API 时使用；空模型走全局 AI_MODEL 环境变量。
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>AI 出题模型</label>
                      <input value={form.depth_ai_model ?? ''} onChange={e => set('depth_ai_model', e.target.value)} style={inputStyle} placeholder="留空=AI_MODEL 环境变量；可填 glm-4.6 等" />
                    </div>
                    <div>
                      <label style={labelStyle}>超时（秒）</label>
                      <input type="number" min={5} max={180} value={form.depth_ai_timeout_sec ?? 30} onChange={e => set('depth_ai_timeout_sec', +e.target.value)} style={{ ...inputStyle, textAlign: 'center' }} />
                    </div>
                    <div>
                      <label style={labelStyle}>最大重试</label>
                      <input type="number" min={0} max={10} value={form.depth_ai_max_retries ?? 3} onChange={e => set('depth_ai_max_retries', +e.target.value)} style={{ ...inputStyle, textAlign: 'center' }} />
                    </div>
                  </div>
                </div>

                {/* 报告附加 hook */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!form.enable_student_course_recommendation}
                      onChange={e => set('enable_student_course_recommendation', e.target.checked)}
                      style={{ width: 14, height: 14 }}
                    />
                    向学员个人报告推送课程
                  </label>
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0 22px' }}>
                    关：学员报告不展示课程推荐；开：报告末尾追加推荐课程模块。
                  </p>
                </div>

                {/* Prompt 入口说明 */}
                <div style={{
                  marginBottom: 20, padding: '10px 14px', borderRadius: 8,
                  background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af',
                }}>
                  ℹ️ V5 出题 / 报告 prompt 硬编码于服务端（v5_question_generator / v5_generator）；
                  「Prompt 配置」页保留是为 V4 旧报告链路独立版本控制,与本策略无关。
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>取消</Button>
                  <Button size="sm" onClick={handleSave} loading={saving}>保存</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const actionBtn = (color) => ({
  padding: '6px 12px', borderRadius: 8, border: `1px solid ${color}30`,
  background: `${color}0f`, color, fontSize: 12, cursor: 'pointer', fontWeight: 500,
});
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };
const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };
